// The Trip Dates screen's words and labels. P5-3 T6.
//
// Pure functions, no React. The SERVICE carries the data — ranks, ties, who is
// available, who has not answered — and the copy is here, which is the split
// `utils/models.py::FlatSelectionRead` already states: *"The service carries
// the data; the sentence is the front end's, because copy is a design decision
// and this is an API."*
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE POLL NEVER PICKS, AND THAT IS A RULE ABOUT THIS FILE TOO
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF's non-negotiable: *"The date poll never picks a winner. Rank by
// count, show ties as ties (`1=`), name the unavailable people on every row,
// and only ever offer a neutral, identical 'Confirm this' on each ... When no
// range is unanimous, say so in plain words at the top."*
//
// So `verdictHeadline` says *"No range works for all 6"* and never *"we
// suggest"*; the tie sentence says outright that there is no arithmetic that
// breaks it; and `rankLabel` renders `1=` for every row on the top count rather
// than choosing between them. There is no `recommended`, no badge, and no
// sorting that puts a thumb on one row.
//
// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ THE ONE PART OF THE DESIGN THIS SLICE CANNOT BUILD: NAMES
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF asks for the unavailable people **by name** on every row, and for
// *"Ask Chri about 10–17 Apr"*. This site has no name source and must not
// invent one:
//
//   * ADR-0003 D2: *"NO USER TABLE, and none is implied here."*
//   * `functions/_lib/discord.js` takes the id and discards the rest — its own
//     comment: *"no username, no avatar, no email, no locale."*
//   * the trip service stores Discord ids, because the bot already keys every
//     store by one.
//
// T4 hit the same wall on the board and recorded it as found-and-not-fixed
// rather than dropping it quietly. It is sharper here, because on this screen
// it is a NON-NEGOTIABLE rather than a nicety, so it is in the PR body as a
// blocked design rule and not as a preference.
//
// What is built instead is the honest half: the reader is the one person this
// site CAN name, so `missingPhrase` says *"you can't"* when the reader is among
// them and counts the rest. Everybody keeps their stable avatar colour from
// `EntryCard.avatarToken` — the same function, not a second one — so "these
// four are the same four" still reads at a glance.

import { avatarToken } from './EntryCard.jsx'

export { avatarToken }

/** `1=` for a tie, `7` otherwise.
 *
 * The service sends `rank` and `tied`; this is the whole of the rendering.
 * Standard competition ranking, so the row after two firsts is `3=` and never
 * `2=` — `trips/tests/test_ranges.py` pins the sequence against HANDOFF's own
 * `1=, 1=, 3=, 3=, 5=, 5=, 7`.
 */
export function rankLabel(row) {
  return row.tied ? `${row.rank}=` : String(row.rank)
}

/** An ISO `YYYY-MM-DD` as a UTC Date, or null.
 *
 * 🔴 UTC, EXPLICITLY, ON BOTH SIDES OF THE ROUND TRIP. `new Date('2027-04-02')`
 * is midnight UTC, and formatting it in a negative-offset zone renders **1
 * April** — a poll row that is one day wrong for anybody west of Greenwich, and
 * right on the machine it was written on. The column is a `DATE`: it has no
 * time and no zone, so it is parsed and printed in one fixed zone and never in
 * the reader's.
 */
function utcDate(iso) {
  if (typeof iso !== 'string') return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return null
  const [, year, month, day] = match
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
}

const part = (options) => new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' })

const WEEKDAY_DAY = part({ weekday: 'short', day: 'numeric' })
const MONTH = part({ month: 'short' })
const YEAR = part({ year: 'numeric' })

/** `Fri 2 → Mon 5 Apr`, the way HANDOFF draws it.
 *
 * The month is printed once when both ends share it and twice when they do not,
 * and the year appears only when the range straddles one — which is the mock's
 * own shape, and the reason the deadline line carries the context instead.
 */
export function formatRange(start, end) {
  const from = utcDate(start)
  const to = utcDate(end)
  if (!from || !to) return ''

  const sameMonth = from.getUTCMonth() === to.getUTCMonth()
  const sameYear = from.getUTCFullYear() === to.getUTCFullYear()

  const left = `${WEEKDAY_DAY.format(from)}${sameMonth && sameYear ? '' : ` ${MONTH.format(from)}`}${
    sameYear ? '' : ` ${YEAR.format(from)}`
  }`
  const right = `${WEEKDAY_DAY.format(to)} ${MONTH.format(to)}${
    sameYear ? '' : ` ${YEAR.format(to)}`
  }`
  return `${left} → ${right}`
}

/** `7 nights · school holidays`, or just the nights when there is no label. */
export function subLine(row) {
  const nights = `${row.nights} ${row.nights === 1 ? 'night' : 'nights'}`
  return row.tag ? `${nights} · ${row.tag}` : nights
}

/** A closing date, as a sentence fragment. Null when the poll has no deadline.
 *
 * ⚠️ It is a DATE THE SCREEN PRINTS AND NOTHING ELSE. Nothing in this slice
 * sweeps, nudges or closes a poll at the deadline — said out loud because a
 * deadline that looks enforced and is not is worse than none.
 */
export function deadlineLabel(deadline) {
  if (typeof deadline !== 'string' || deadline === '') return null
  const when = new Date(deadline)
  if (Number.isNaN(when.getTime())) return null
  return `answers wanted by ${part({ weekday: 'long', day: 'numeric', month: 'short' }).format(when)}`
}

/**
 * Who cannot make this one, in the words this site can honestly use.
 *
 * See the header: there are no names here and there is no name source to get
 * them from. The reader is the exception, and naming them is the half that
 * still does the design's job — *"can I make this one?"* is answered without
 * counting avatars.
 */
export function missingPhrase(row, you) {
  const missing = row.unavailable ?? []
  if (missing.length === 0) return 'everyone who has answered can make it'
  const includesYou = you != null && missing.includes(you)
  const others = missing.filter((id) => id !== you).length
  if (includesYou && others === 0) return "you can't make it"
  if (includesYou) return `you and ${others} ${others === 1 ? 'other' : 'others'} can't make it`
  return `${others} ${others === 1 ? 'person' : 'people'} can't make it`
}

/** `5 of 6 can make it` — the denominator is the whole roster, never the
 * respondents, so a count cannot look final while people are still answering. */
export function countLabel(row, poll) {
  return `${row.available_count} of ${poll.roster.length} can make it`
}

/** `−1`, or `no gap`. HANDOFF's right-hand marker. */
export function gapLabel(row) {
  const missing = (row.unavailable ?? []).length
  return missing === 0 ? 'no gap' : `−${missing}`
}

/** How much of the bar to fill, as a percentage of the whole roster. */
export function barWidth(row, poll) {
  const total = poll.roster.length || 1
  return `${Math.round((row.available_count / total) * 100)}%`
}

/** `all 6 answered · this is the whole picture`, or the honest alternative.
 *
 * 🔴 *"counts can only go up"* is the half that matters. A poll four people
 * have answered is not a smaller version of the finished one — it is a
 * provisional one, and every count on the screen can still rise. Saying so is
 * what stops an organiser confirming a five-of-six that was really a
 * three-of-six.
 */
export function answeredLine(poll) {
  const total = poll.roster.length
  const answered = poll.respondents.length
  if (answered === total) return `all ${total} answered · this is the whole picture`
  const missing = total - answered
  return (
    `${answered} of ${total} answered · ` +
    `${missing} ${missing === 1 ? 'has' : 'have'} not · counts can only go up`
  )
}

/** The plain-words verdict at the top of the organiser's results.
 *
 * HANDOFF: *"When no range is unanimous, say so in plain words at the top."*
 * Three states, and none of them recommends anything.
 */
export function verdictHeadline(poll) {
  const total = poll.roster.length
  if (poll.respondents.length === 0) return 'Nobody has answered yet.'
  if (poll.unanimous) return `One range works for all ${total}.`
  return `No range works for all ${total}.`
}

export function verdictBody(poll, you) {
  if (poll.respondents.length === 0) {
    return (
      `${poll.candidate_ranges.length} ranges are up. Nothing is ranked until somebody ` +
      `taps something, and nothing here picks for you when they do.`
    )
  }
  if (poll.unanimous) {
    return 'Confirm it, or keep it open a day in case someone changes their mind.'
  }

  const top = poll.results.filter((row) => row.is_top)
  const described = top
    .map((row) => `${formatRange(row.start, row.end)} (${missingPhrase(row, you)})`)
    .join(', and ')
  const opening =
    top.length > 1
      ? `${top.length} come closest, all at ${poll.best_count} of ${poll.roster.length}: `
      : `The best is ${poll.best_count} of ${poll.roster.length}: `
  const closing =
    top.length > 1
      ? " There's no arithmetic that picks between them — someone misses it, or you propose a week nobody has seen yet."
      : ' Someone misses it, or you propose a week nobody has seen yet.'
  return `${opening}${described}.${closing}`
}

/**
 * Who is hardest to fit, ascending — so the constraint is first.
 *
 * Derived from `results` rather than asked for, because the service already
 * sends every row's availability and a second endpoint for the same arithmetic
 * is a second answer waiting to disagree.
 *
 * Somebody who has not answered gets `count: null` rather than 0. They are not
 * a person nothing suits; they are a person nobody has heard from, and drawing
 * them at the top of a "hardest to fit" list would send the organiser to chase
 * the wrong problem.
 */
export function hardestToFit(poll) {
  const answered = new Set(poll.respondents)
  const rows = poll.roster.map((userId) => ({
    userId,
    count: answered.has(userId)
      ? poll.results.filter((row) => row.available.includes(userId)).length
      : null,
    total: poll.candidate_ranges.length,
  }))
  return rows.sort((a, b) => {
    if (a.count === null && b.count === null) return 0
    if (a.count === null) return 1
    if (b.count === null) return -1
    return a.count - b.count
  })
}

/** The phone's Send button. `null` picks is "has not answered yet". */
export function sendLabel(picks, total) {
  if (picks.length === 0) return 'Send · none of these work'
  return `Send · ${picks.length} of ${total}`
}

/** What the member's own answer says back to them once it is in.
 *
 * `null` and `[]` are different answers — never having sent one, and having
 * sent *"none of these work for me"*. The service keeps them apart and so does
 * this: collapsing them would show somebody their unsent poll as sent.
 */
export function sentSummary(selection, total) {
  if (selection === null) return null
  if (selection.length === 0) return 'None of these work for you.'
  return `${selection.length} of ${total} work for you.`
}
